import "@/index.css";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, Instagram, Calendar, Clock, ArrowLeft, ArrowRight, Check, Loader2 } from "lucide-react";
import { Toaster, toast } from "sonner";
import { useSiteData, SiteDataProvider } from "../context/SiteDataContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
// Only show Turnstile if we have a valid key configured
const TURNSTILE_ENABLED = TURNSTILE_SITE_KEY && !window.location.hostname.includes('preview.emergentagent');

const NYZA_LOGO_URL = "/images/nyza-logo.webp";

// Navigation Component - Same style as main page
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = window.location.pathname;

  const isActive = (path) => {
    if (path === '/schedule-demo') return location === '/schedule-demo';
    if (path === '/references') return location === '/references';
    if (path === '/') return location === '/';
    return false;
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 py-4 md:py-6 bg-gradient-to-b from-white/80 to-transparent">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="relative z-10">
            <img 
              src={NYZA_LOGO_URL} 
              alt="NYZA Studio" 
              className="h-8 md:h-10"
              style={{ filter: 'brightness(0)' }}
            />
          </Link>
          
          {/* Desktop Nav */}
          <div className="hidden md:flex items-center bg-white/90 backdrop-blur-md rounded-full px-2 py-1.5 shadow-md border border-gray-200">
            <Link 
              to="/"
              className={`px-4 py-1.5 text-xs font-medium uppercase tracking-wide transition-all duration-200 rounded-full ${isActive('/') ? 'text-gray-900 bg-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
            >
              HOME
            </Link>
            <Link 
              to="/references"
              className={`px-4 py-1.5 text-xs font-medium uppercase tracking-wide transition-all duration-200 rounded-full ${isActive('/references') ? 'text-gray-900 bg-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
            >
              REFERENCES
            </Link>
            <Link 
              to="/#products"
              className="px-4 py-1.5 text-xs font-medium uppercase tracking-wide transition-all duration-200 rounded-full text-gray-600 hover:text-gray-900"
            >
              PRODUCTS
            </Link>
            <Link 
              to="/#marketing"
              className="px-4 py-1.5 text-xs font-medium uppercase tracking-wide transition-all duration-200 rounded-full text-gray-600 hover:text-gray-900"
            >
              MARKETING
            </Link>
          </div>
          
          {/* Contact Button */}
          <Link to="/#contact" className="hidden md:block bg-[#9A45FF] text-white text-xs font-medium uppercase tracking-wide px-5 py-2 rounded-full hover:bg-[#8035E8] transition-colors">
            CONTACT
          </Link>
          
          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2 text-gray-900" 
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        
        {/* Mobile Menu */}
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden mt-4 bg-white rounded-2xl shadow-lg border border-gray-100 p-4"
          >
            <Link 
              to="/" 
              className="block py-2 text-sm text-gray-900 font-medium uppercase tracking-wide hover:text-purple-600 transition-colors"
              onClick={() => setIsOpen(false)}
            >
              HOME
            </Link>
            <Link 
              to="/references" 
              className="block py-2 text-sm text-gray-900 font-medium uppercase tracking-wide hover:text-purple-600 transition-colors"
              onClick={() => setIsOpen(false)}
            >
              REFERENCES
            </Link>
            <Link 
              to="/#products" 
              className="block py-2 text-sm text-gray-900 font-medium uppercase tracking-wide hover:text-purple-600 transition-colors"
              onClick={() => setIsOpen(false)}
            >
              PRODUCTS
            </Link>
            <Link 
              to="/#marketing" 
              className="block py-2 text-sm text-gray-900 font-medium uppercase tracking-wide hover:text-purple-600 transition-colors"
              onClick={() => setIsOpen(false)}
            >
              MARKETING
            </Link>
            <Link
              to="/#contact"
              className="block w-full text-center mt-3 bg-[#9A45FF] text-white text-sm font-medium uppercase tracking-wide px-4 py-2 rounded-full"
              onClick={() => setIsOpen(false)}
            >
              CONTACT
            </Link>
          </motion.div>
        )}
      </div>
    </nav>
  );
};

// Calendar Component
const CalendarPicker = ({ selectedDate, onDateSelect, availableSlots }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();
  const adjustedFirstDay = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1; // Monday start
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const isDateSelectable = (day) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    date.setHours(0, 0, 0, 0);
    return date >= today;
  };

  const formatDate = (day) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return date.toISOString().split('T')[0];
  };

  const isSelected = (day) => {
    if (!selectedDate) return false;
    return formatDate(day) === selectedDate;
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <button 
          onClick={prevMonth}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          disabled={currentMonth.getMonth() === today.getMonth() && currentMonth.getFullYear() === today.getFullYear()}
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <h3 className="text-lg font-semibold text-gray-900">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>
        <button 
          onClick={nextMonth}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowRight className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-2">
        {dayNames.map(day => (
          <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: adjustedFirstDay }).map((_, i) => (
          <div key={`empty-${i}`} className="aspect-square" />
        ))}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const selectable = isDateSelectable(day);
          const selected = isSelected(day);
          
          return (
            <button
              key={day}
              onClick={() => selectable && onDateSelect(formatDate(day))}
              disabled={!selectable}
              className={`
                aspect-square rounded-lg flex items-center justify-center text-sm font-medium transition-all
                ${selectable 
                  ? selected 
                    ? 'bg-purple-600 text-white' 
                    : 'hover:bg-purple-50 text-gray-900 hover:text-purple-600'
                  : 'text-gray-300 cursor-not-allowed'
                }
              `}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Time Slots Component
const TimeSlots = ({ slots, selectedSlot, onSlotSelect, loading }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
          <span className="ml-2 text-gray-500">Loading times...</span>
        </div>
      </div>
    );
  }

  if (!slots || slots.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-5 h-5 text-purple-600" />
          <h3 className="text-lg font-semibold text-gray-900">Available Times</h3>
        </div>
        <p className="text-gray-500 text-center py-8">
          Please select a date first
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <Clock className="w-5 h-5 text-purple-600" />
        <h3 className="text-lg font-semibold text-gray-900">Available Times</h3>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[300px] overflow-y-auto">
        {slots.map(slot => (
          <button
            key={slot.datetime}
            onClick={() => onSlotSelect(slot)}
            className={`
              px-3 py-2 rounded-lg text-sm font-medium transition-all
              ${selectedSlot?.datetime === slot.datetime
                ? 'bg-purple-600 text-white'
                : 'bg-gray-50 text-gray-700 hover:bg-purple-50 hover:text-purple-600'
              }
            `}
          >
            {slot.time}
          </button>
        ))}
      </div>
    </div>
  );
};

// App Selection Component
const AppSelector = ({ apps, selectedApp, onAppSelect }) => {
  if (!apps || apps.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Select a System</h3>
      {apps.map(app => (
        <button
          key={app.id}
          onClick={() => onAppSelect(app)}
          className={`
            w-full flex items-start gap-4 p-4 rounded-xl border-2 transition-all text-left
            ${selectedApp?.id === app.id
              ? 'border-purple-600 bg-purple-50'
              : 'border-gray-200 hover:border-purple-300 bg-white'
            }
          `}
        >
          <img 
            src={app.image} 
            alt={app.name}
            className="w-14 h-14 rounded-xl object-cover flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="font-semibold text-gray-900">{app.name}</h4>
              {selectedApp?.id === app.id && (
                <Check className="w-4 h-4 text-purple-600" />
              )}
            </div>
            <p className="text-sm text-gray-500 mt-1">{app.description}</p>
          </div>
        </button>
      ))}
    </div>
  );
};

// Contact Form Component
const ContactForm = ({ formData, onChange, onSubmit, isSubmitting, selectedApp, selectedSlot }) => {
  const [turnstileReady, setTurnstileReady] = useState(false);
  
  useEffect(() => {
    // Only load Turnstile script if enabled
    if (!TURNSTILE_ENABLED) return;
    
    if (!document.querySelector('script[src*="turnstile"]')) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.onload = () => setTurnstileReady(true);
      document.head.appendChild(script);
    } else {
      setTurnstileReady(true);
    }
  }, []);

  const handleTurnstileCallback = (token) => {
    onChange({ ...formData, turnstile_token: token });
  };

  // Make callback available globally
  useEffect(() => {
    if (!TURNSTILE_ENABLED) return;
    window.onTurnstileSuccess = handleTurnstileCallback;
    return () => { delete window.onTurnstileSuccess; };
  }, [formData]); // eslint-disable-line react-hooks/exhaustive-deps

  const isFormValid = formData.name && formData.email && formData.phone && selectedApp && selectedSlot;

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => onChange({ ...formData, name: e.target.value })}
          required
          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all"
          placeholder="Your name"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
        <input
          type="email"
          value={formData.email}
          onChange={(e) => onChange({ ...formData, email: e.target.value })}
          required
          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all"
          placeholder="your@email.com"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
        <input
          type="tel"
          value={formData.phone}
          onChange={(e) => onChange({ ...formData, phone: e.target.value })}
          required
          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all"
          placeholder="+1 ..."
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Message (optional)</label>
        <textarea
          value={formData.message}
          onChange={(e) => onChange({ ...formData, message: e.target.value })}
          rows={3}
          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all resize-none"
          placeholder="Tell us more about your project..."
        />
      </div>

      {/* Turnstile Widget - only if enabled */}
      {TURNSTILE_ENABLED && turnstileReady && (
        <div 
          className="cf-turnstile" 
          data-sitekey={TURNSTILE_SITE_KEY}
          data-callback="onTurnstileSuccess"
          data-theme="light"
        />
      )}

      {/* Summary */}
      {selectedApp && selectedSlot && (
        <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
          <h4 className="font-medium text-purple-900 mb-2">Summary</h4>
          <p className="text-sm text-purple-700">
            <strong>System:</strong> {selectedApp.name}<br />
            <strong>Date:</strong> {new Date(selectedSlot.datetime).toLocaleDateString('en-US', { 
              weekday: 'long', 
              day: 'numeric', 
              month: 'long',
              year: 'numeric'
            })} at {selectedSlot.time}<br />
            <strong>Duration:</strong> 30 minutes
          </p>
        </div>
      )}

      <button
        type="submit"
        disabled={!isFormValid || isSubmitting}
        className={`
          w-full py-4 rounded-xl font-semibold text-white transition-all
          ${isFormValid && !isSubmitting
            ? 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 shadow-lg hover:shadow-xl'
            : 'bg-gray-300 cursor-not-allowed'
          }
        `}
      >
        {isSubmitting ? (
          <span className="flex items-center justify-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin" />
            Sending...
          </span>
        ) : (
          'Request Demo'
        )}
      </button>
    </form>
  );
};

// Main Page Component
const ScheduleDemoContent = () => {
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    turnstile_token: ''
  });

  // Fetch apps on mount
  useEffect(() => {
    fetch(`${API}/demo/apps`)
      .then(res => res.json())
      .then(data => {
        setApps(data);
        if (data.length === 1) {
          setSelectedApp(data[0]);
        }
      })
      .catch(err => console.error('Error fetching apps:', err));
  }, []);

  // Fetch slots when date changes
  useEffect(() => {
    if (!selectedDate) {
      setSlots([]);
      return;
    }

    setLoadingSlots(true);
    setSelectedSlot(null);
    
    fetch(`${API}/demo/slots?date=${selectedDate}`)
      .then(res => res.json())
      .then(data => {
        setSlots(data);
        setLoadingSlots(false);
      })
      .catch(err => {
        console.error('Error fetching slots:', err);
        setLoadingSlots(false);
      });
  }, [selectedDate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedApp || !selectedSlot) {
      toast.error('Please select a system and a time slot');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API}/demo/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          app_id: selectedApp.id,
          requested_slot: selectedSlot.datetime
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Demo request sent successfully! We will contact you soon.');
        // Reset form
        setFormData({ name: '', email: '', phone: '', message: '', turnstile_token: '' });
        setSelectedSlot(null);
        setSelectedDate(null);
        // Reload Turnstile
        if (window.turnstile) {
          window.turnstile.reset();
        }
      } else {
        toast.error(data.detail || 'Error sending request');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Connection error. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navigation />
      <Toaster position="top-center" richColors />
      
      {/* Hero Section */}
      <div className="pt-28 pb-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Schedule a Demo
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Experience our solutions live in a personal demo. Select a system, 
              choose a time slot, and we'll show you how we can help grow your business.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 pb-20">
        <div className="grid lg:grid-cols-3 gap-8">
          
          {/* Left Column - App Selection */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="lg:col-span-1"
          >
            <AppSelector 
              apps={apps} 
              selectedApp={selectedApp} 
              onAppSelect={setSelectedApp} 
            />
          </motion.div>

          {/* Middle Column - Calendar & Slots */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="lg:col-span-1 space-y-6"
          >
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-5 h-5 text-purple-600" />
              <h3 className="text-lg font-semibold text-gray-900">Select a Date</h3>
            </div>
            <CalendarPicker 
              selectedDate={selectedDate}
              onDateSelect={setSelectedDate}
            />
            <TimeSlots 
              slots={slots}
              selectedSlot={selectedSlot}
              onSlotSelect={setSelectedSlot}
              loading={loadingSlots}
            />
          </motion.div>

          {/* Right Column - Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="lg:col-span-1"
          >
            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm sticky top-24">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Your Contact Details</h3>
              <ContactForm 
                formData={formData}
                onChange={setFormData}
                onSubmit={handleSubmit}
                isSubmitting={isSubmitting}
                selectedApp={selectedApp}
                selectedSlot={selectedSlot}
              />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-gray-400">
            © {new Date().getFullYear()} NYZA Studio. All rights reserved.
          </p>
          <div className="flex justify-center gap-6 mt-4">
            <Link to="/impressum" className="text-gray-400 hover:text-white transition-colors text-sm">Impressum</Link>
            <Link to="/datenschutz" className="text-gray-400 hover:text-white transition-colors text-sm">Datenschutz</Link>
            <Link to="/agb" className="text-gray-400 hover:text-white transition-colors text-sm">AGB</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Wrapped component with provider
export default function ScheduleDemo() {
  return (
    <SiteDataProvider>
      <ScheduleDemoContent />
    </SiteDataProvider>
  );
}
